import React, { useRef, useEffect } from 'react';
import { EditorView } from '@codemirror/view';
import './App.css';
import { HeaderBar } from './features/settings/HeaderBar';
import { SidebarSection } from './features/layout/SidebarSection';
import { EditorSection } from './features/layout/EditorSection';
import { PreviewSection } from './features/layout/PreviewSection';
import { useAuth } from './hooks/useAuth';
import { useProjectsState } from './hooks/useProjectsState';
import { usePreview } from './hooks/usePreview';
import { useTaskRunner } from './hooks/useTaskRunner';
import { useTreeManager } from './hooks/useTreeManager';
import { useFileManager } from './hooks/useFileManager';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useResize } from './hooks/useResize';
import { useEditorResize } from './hooks/useEditorResize';
import { useCommands } from './hooks/useCommands';
import { useEditorExtensions } from './hooks/useEditorExtensions';
import { useFetchTree } from './hooks/useFetchTree';
import { useSettingsIO } from './hooks/useSettingsIO';
import { useEditorConfig } from './hooks/useEditorConfig';
import { useNavigation } from './hooks/useNavigation';
import { useNewEntry } from './hooks/useNewEntry';
import { useDiffView } from './hooks/useDiffView';
import { useFileContent } from './hooks/useFileContent';
import { useApiSync } from './hooks/useApiSync';
import { useEsbuild } from './hooks/useEsbuild';
import { useFormat } from './hooks/useFormat';
import { useCacheManager } from './hooks/useCacheManager';
import { useFileCache } from './hooks/useFileCache';
import { useGoToResults } from './hooks/useGoToResults';
import { useLayoutPanels } from './hooks/useLayoutPanels';
import { useAppCallbacks } from './hooks/useAppCallbacks';
import { useHeaderProps } from './hooks/useHeaderProps';
import { useSideEffects } from './hooks/useSideEffects';
import { useRenderCallbacks } from './hooks/useRenderCallbacks';
import { useGoToDefinition, PeekDefinitionData } from './hooks/useGoToDefinition';
import { useGit } from './hooks/useGit';
import { useExternalChangeDetection } from './hooks/useExternalChangeDetection';
import { useNewFileDetection } from './hooks/useNewFileDetection';
import { useFindReferences, Reference } from './hooks/useFindReferences';
import { useRecentFiles } from './hooks/useRecentFiles';
import { useFavoriteFiles } from './hooks/useFavoriteFiles';
import { ModalContainer } from './features/modals/ModalContainer';
import { helpTopics } from './data/helpTopics';
import { useSettings, useUI, usePreviewContext, useEditorState } from './contexts';
import { PROJECT_ID, STORAGE_ACTIVE, commitHistorySeed } from './constants';
import { exportProjectAsZip, readZipFile, normalizeImportedPaths, importedFilesToRecord } from './utils/zipExport';
import { openInCodeSandbox, openInStackBlitz } from './utils/sandboxExport';
import { ColorBlindFilters } from './components/ColorBlindFilters';
import { OfflineIndicator } from './components/OfflineIndicator';
import { SkipLinks } from './components/SkipLinks';
import { createAriaLiveRegion } from './utils/accessibility';
import { usePanelNavigation } from './hooks/useKeyboardNavigation';
import { useStatusAnnouncements } from './hooks/useAnnouncements';

function App() {
  // Settings from context
  const { apiBase, apiKey, apiSecret, setIndentSize, setEncoding, blameEnabled } = useSettings();

  // UI from context
  const {
    panelOrder,
    globalError,
    zenMode,
    setReferencesOpen,
    setReferencesSymbol,
    setReferencesResults,
    setReferencesHighlight,
    setDiffViewOpen,
    setDiffViewFile,
    setRenameModalOpen,
    setRenameSymbol,
    setRenameNewName,
  } = useUI();

  // Keyboard shortcuts hook (uses UIContext internally)
  useKeyboardShortcuts();

  // F2 - Rename symbol (requires editorViewRef)
  React.useEffect(() => {
    const handleF2 = (e: KeyboardEvent) => {
      if (e.key === 'F2' && editorViewRef.current) {
        e.preventDefault();
        const view = editorViewRef.current;
        const { state } = view;
        const pos = state.selection.main.head;
        const line = state.doc.lineAt(pos);
        const text = line.text;
        const lineOffset = pos - line.from;

        // Find word boundaries
        let start = lineOffset;
        let end = lineOffset;
        const isWordChar = (ch: string) => /[\w$]/.test(ch);

        while (start > 0 && isWordChar(text[start - 1])) start--;
        while (end < text.length && isWordChar(text[end])) end++;

        if (start !== end) {
          const word = text.slice(start, end);
          setRenameSymbol(word);
          setRenameNewName(word);
          setRenameModalOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleF2);
    return () => window.removeEventListener('keydown', handleF2);
  }, [setRenameSymbol, setRenameNewName, setRenameModalOpen]);

  // Initialize ARIA live region for screen reader announcements
  useEffect(() => {
    createAriaLiveRegion();
  }, []);

  // Panel navigation for accessibility (Cmd+1/2/3 to focus panels)
  usePanelNavigation();

  // Resize hook for sidebar/preview panels (uses UIContext internally)
  const { startResize } = useResize();

  // Editor resize hook for vertical resizing (uses UIContext internally)
  const { startEditorResize } = useEditorResize();

  const {
    projects,
    activeProject,
    projectOverride,
    setProjectOverride,
    applyProjectOverride,
    branches,
    activeBranch,
    setActiveBranch,
    projectCaches,
  } = useProjectsState(PROJECT_ID, ['main', 'develop', 'feature/ui']);

  // Preview and Editor contexts used by child components
  usePreviewContext();
  const { code, setCode, activeFile, setActiveFile, openTabs, setOpenTabs } = useEditorState();

  // UI context for status and conflict modal
  const { status, setStatus, setConflictResolutionModalOpen } = useUI();

  // Announce status changes to screen readers
  useStatusAnnouncements(status);

  // File cache hook (manages persistedFiles, workingFiles, fetchedFiles, cacheVersion)
  const {
    persistedFiles,
    setPersistedFiles,
    workingFiles,
    setWorkingFiles,
    fetchedFiles,
    setFetchedFiles,
    cacheVersion,
    setCacheVersion,
    handleTreeDeleteCleanup,
    handleTreeMoveCleanup,
  } = useFileCache({ initialFiles: projectCaches.files, initialWorking: projectCaches.working });

  // External change detection hook
  const {
    conflicts,
    resolveConflict,
    resolveAllConflicts,
  } = useExternalChangeDetection({
    storageKey: `atlas-ide-${PROJECT_ID}-files`,
    files: { ...persistedFiles, ...workingFiles },
    onConflict: (newConflicts) => {
      if (newConflicts.length > 0) {
        setConflictResolutionModalOpen(true);
        setStatus(`${newConflicts.length} file conflict${newConflicts.length !== 1 ? 's' : ''} detected`);
      }
    },
    onExternalChange: (path, content) => {
      setWorkingFiles(prev => ({ ...prev, [path]: content }));
      setStatus(`External change applied: ${path}`);
    },
  });

  const {
    tree,
    setTree,
    treeIndex,
    setTreeIndex,
    collapsed,
    setCollapsed,
    treeFilter,
    setTreeFilter,
    filteredTree,
    fileList,
    selectedNodeId,
    setSelectedNodeId,
    multiSelectedIds,
    contextMenu,
    applyTreeUpdate,
    closeContextMenu,
    handleContextMenu,
    handleSelectNode,
    handleToggleCollapse,
    handleRename,
    handleDelete,
    handleMove,
    handleCopy,
    handleCut,
    handlePaste,
    handleDuplicate,
    clipboard,
    resolvePath,
    fileTypeFilter,
    setFileTypeFilter,
    availableFileTypes,
  } = useTreeManager({
    initialSelectedNodeId: localStorage.getItem(STORAGE_ACTIVE),
    onDeleteCleanup: handleTreeDeleteCleanup,
    onMoveCleanup: handleTreeMoveCleanup,
  });

  const editorViewRef = useRef<EditorView | null>(null);
  const importSettingsInputRef = useRef<HTMLInputElement | null>(null);

  // Esbuild initialization hook (uses UIContext internally)
  const { esbuildReady } = useEsbuild();

  // GoTo file results hook (uses UIContext internally)
  const { goToResults } = useGoToResults({ fileList });

  // Editor extensions hook (uses contexts internally)
  const { languageExtensions, lintExtension, getLocationFromLog } = useEditorExtensions({
    resolvePath,
  });

  // Diff view hook (uses EditorStateContext internally)
  const { diffLines } = useDiffView({
    persistedFiles,
    workingFiles,
  });

  const { token, serverState, status: authStatus, authFetch, refreshToken } = useAuth(apiBase, apiKey, apiSecret);

  // Git hook (uses workingFiles and persistedFiles)
  const {
    gitState,
    stagedFiles: gitStagedFiles,
    unstagedFiles: gitUnstagedFiles,
    commitMessage: gitCommitMessage,
    setCommitMessage: setGitCommitMessage,
    isCommitting: gitIsCommitting,
    isPushing: gitIsPushing,
    isPulling: gitIsPulling,
    isFetching: gitIsFetching,
    isSyncing: gitIsSyncing,
    commits: gitCommits,
    isLoadingCommits: gitIsLoadingCommits,
    blameData: gitBlameData,
    fetchCommitLog: gitFetchCommitLog,
    fetchBlame: gitFetchBlame,
    viewFileAtCommit: gitViewFileAtCommit,
    stageFile: gitStageFile,
    unstageFile: gitUnstageFile,
    stageAll: gitStageAll,
    unstageAll: gitUnstageAll,
    discardFile: gitDiscardFile,
    commit: gitCommit,
    push: gitPush,
    pull: gitPull,
    fetch: gitFetch,
    sync: gitSync,
    switchBranch: gitSwitchBranch,
    createBranch: gitCreateBranch,
    deleteBranch: gitDeleteBranch,
    getStatusIcon: getGitStatusIcon,
    getStatusColor: getGitStatusColor,
  } = useGit({
    apiBase,
    activeProject,
    token,
    authFetch,
    workingFiles,
    persistedFiles,
  });

  const { hasTaskEndpoint, handleLint, handleTest: handleRun, handleBuild, handleRunAll } = useTaskRunner({
    activeProject,
    token,
    apiBase,
    authFetch,
  });

  const {
    isDirty,
    activePath,
    handleSave,
    handleOpenFile,
    handleSelectTab,
    handleCloseTab,
    handleCodeChange,
  } = useFileManager({
    activeProject,
    treeIndex,
    token,
    apiBase,
    authFetch,
    persistedFiles,
    setPersistedFiles,
    workingFiles,
    setWorkingFiles,
    fetchedFiles,
    setFetchedFiles,
    resolvePath,
  });

  // Combined side effects hook (uses contexts internally)
  useSideEffects({
    setSelectedNodeId,
    authStatus,
    workingFiles,
    isDirty,
  });

  // Fetch blame data when active file changes (if blame is enabled)
  React.useEffect(() => {
    if (blameEnabled && activePath) {
      void gitFetchBlame(activePath);
    }
  }, [blameEnabled, activePath, gitFetchBlame]);

  // Get current file's blame data
  const currentBlameData = activePath ? gitBlameData[activePath] || [] : [];

  // Recent files hook
  const { recentFiles, addRecentFile, removeRecentFile, clearRecentFiles } = useRecentFiles();

  // Favorite files hook
  const { favoriteFiles, isFavorite, toggleFavorite, removeFavorite, clearFavorites } = useFavoriteFiles();

  // Track file opens in recent files
  React.useEffect(() => {
    if (activePath) {
      addRecentFile(activePath);
    }
  }, [activePath, addRecentFile]);

  // Handler to open a file from recent/favorite files list
  const handleOpenRecentFile = React.useCallback((path: string) => {
    const node = treeIndex.byPath[path];
    if (node) {
      handleOpenFile(node.id);
    }
  }, [treeIndex, handleOpenFile]);

  // Fetch tree hook (uses contexts internally)
  const { fetchTree, handleReloadProject } = useFetchTree({
    token,
    apiBase,
    activeProject,
    cacheVersion,
    authFetch,
    setTree,
    setTreeIndex,
  });

  // New file detection hook - auto-refresh tree on new files
  useNewFileDetection({
    files: { ...persistedFiles, ...workingFiles },
    tree,
    setTree,
    setTreeIndex,
    onNewFile: (path) => {
      setStatus(`New file detected: ${path.split('/').pop()}`);
    },
    onDeletedFile: (path) => {
      setStatus(`File removed: ${path.split('/').pop()}`);
    },
  });

  // File content fetching hook (uses contexts internally)
  useFileContent({
    activeProject,
    token,
    apiBase,
    authFetch,
    persistedFiles,
    workingFiles,
    fetchedFiles,
    resolvePath,
    setPersistedFiles,
    setWorkingFiles,
    setFetchedFiles,
  });

  // Format hook (uses contexts internally)
  const { handleFormat } = useFormat({
    resolvePath,
    setWorkingFiles,
  });

  // API base/credentials sync hook (uses contexts internally)
  useApiSync({
    refreshToken,
    setCacheVersion,
  });

  // Cache manager hook (uses contexts internally)
  const { handleClearCache } = useCacheManager({
    activeProject,
    projectCaches,
    setPersistedFiles,
    setWorkingFiles,
    setFetchedFiles,
    setSelectedNodeId,
    setCacheVersion,
  });

  // Preview hook (uses contexts internally)
  const { previewHtml, previewError, previewSize } = usePreview({ esbuildReady });

  // Export project as ZIP
  const handleExportProject = React.useCallback(() => {
    // Combine persisted and working files, with working files taking priority
    const allFiles = { ...persistedFiles, ...workingFiles };
    exportProjectAsZip(allFiles, activeProject || 'project');
  }, [persistedFiles, workingFiles, activeProject]);

  // Import project from ZIP
  const handleImportProject = React.useCallback(async () => {
    // Create a hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        // Read and parse the ZIP file
        const importedFiles = await readZipFile(file);
        const normalizedFiles = normalizeImportedPaths(importedFiles);
        const filesRecord = importedFilesToRecord(normalizedFiles);

        // Merge with existing working files
        setWorkingFiles((prev) => ({ ...prev, ...filesRecord }));

        // Refresh the tree to show new files
        await fetchTree();

        // Show success status
        const fileCount = Object.keys(filesRecord).length;
        console.log(`Imported ${fileCount} files from ${file.name}`);
      } catch (error) {
        console.error('Failed to import ZIP file:', error);
      }
    };
    input.click();
  }, [setWorkingFiles, fetchTree]);

  // Export to CodeSandbox
  const handleExportToCodeSandbox = React.useCallback(() => {
    const allFiles = { ...persistedFiles, ...workingFiles };
    openInCodeSandbox(allFiles);
  }, [persistedFiles, workingFiles]);

  // Export to StackBlitz
  const handleExportToStackBlitz = React.useCallback(() => {
    const allFiles = { ...persistedFiles, ...workingFiles };
    openInStackBlitz(allFiles, activeProject || 'project');
  }, [persistedFiles, workingFiles, activeProject]);

  // Initialize project settings (.atlas-ide.json)
  const handleInitProjectSettings = React.useCallback(() => {
    const configFile = '.atlas-ide.json';
    const existingContent = workingFiles[configFile] || persistedFiles[configFile];

    if (existingContent) {
      // File exists, open it for editing
      setActiveFile(configFile);
      if (!openTabs.includes(configFile)) {
        setOpenTabs(prev => [...prev, configFile]);
      }
      setCode(existingContent);
      setStatus('Project settings file opened');
    } else {
      // Create default config
      const defaultSettings = {
        theme: 'dark',
        fontSize: 14,
        indentSize: 2,
        indentStyle: 'spaces',
        minimap: false,
        vimMode: false,
        wordWrap: true,
        lineNumbers: true,
        autoSave: true,
        autoSaveDelay: 1000,
        formatOnSave: false,
        hotReload: true,
        previewDelay: 500,
        exclude: ['node_modules', '.git', 'dist', 'build', 'coverage'],
        lintOnSave: true,
        gitBlame: false,
      };
      const content = JSON.stringify(defaultSettings, null, 2);
      setWorkingFiles(prev => ({ ...prev, [configFile]: content }));
      setActiveFile(configFile);
      setOpenTabs(prev => [...prev, configFile]);
      setCode(content);
      setStatus('Created .atlas-ide.json');
    }
  }, [workingFiles, persistedFiles, openTabs, setActiveFile, setOpenTabs, setCode, setStatus, setWorkingFiles]);

  // Suppress unused variable warnings for editor state destructuring
  void code;
  void activeFile;

  // Commands hook (uses contexts internally)
  const { filteredCommands, toggleAutosave, toggleTheme } = useCommands({
    handleLint,
    handleRun,
    handleBuild,
    handleClearCache,
    handleReloadProject,
    handleExportProject,
    handleImportProject,
    handleExportToCodeSandbox,
    handleExportToStackBlitz,
    handleInitProjectSettings,
  });

  // Navigation hook (uses contexts internally)
  const { pendingLocation, setPendingLocation, goToEditorLocation, handleNavigateToLocation } = useNavigation({
    treeIndex,
    editorViewRef,
  });

  // New entry hook (uses UIContext internally)
  const {
    newEntryDialog,
    setNewEntryDialog,
    newEntryName,
    setNewEntryName,
    newEntryTemplate,
    setNewEntryTemplate,
    handleNewEntry,
    handleCreateDirectory,
    handleCreateFile,
    handleAddProject,
  } = useNewEntry({
    tree,
    contextMenu,
    closeContextMenu,
    fetchTree,
  });

  // Layout panels hook (uses UIContext internally)
  const { getDropProps, getDragHandleProps, getPanelStyle, getPanelClassName, getDividerProps } = useLayoutPanels({
    startResize,
  });

  // Settings IO hook (uses contexts internally)
  const { handleExportSettings, handleImportSettings, handleApplyProjectOverride, triggerImport } = useSettingsIO({
    activeProject,
    projectOverride,
    activeBranch,
    setActiveBranch,
    setProjectOverride,
    applyProjectOverride,
    setPersistedFiles,
    setWorkingFiles,
    setFetchedFiles,
    setCacheVersion,
    importSettingsInputRef,
  });

  // App callbacks hook (uses contexts internally)
  const { handleSelectGoTo, handleSelectCommand, handleCommit, handleChangeBranch, handleEditorBlur, handleGlobalSearchSelect, handleSymbolSelect } = useAppCallbacks({
    handleOpenFile,
    setActiveBranch,
    isDirty,
    handleSave,
    treeIndex,
    setPendingLocation,
    goToEditorLocation,
  });

  // Render callbacks hook (uses contexts internally)
  const {
    toggleShowCreateMenu,
    handleNewFile,
    handleNewFolder,
    handleStartEditorResize,
    handleRestartPreview,
    handleToggleSandbox,
  } = useRenderCallbacks({
    handleNewEntry,
    startEditorResize,
  });

  // Get Peek Definition state from UI context
  const {
    setPeekDefinitionOpen,
    setPeekDefinitionData,
  } = useUI();

  // Go to Definition hook
  const { goToDefinitionExtension } = useGoToDefinition({
    workingFiles,
    treeIndex,
    resolvePath,
    onNavigate: goToEditorLocation,
    onPeekDefinition: React.useCallback((data: PeekDefinitionData) => {
      setPeekDefinitionData(data);
      setPeekDefinitionOpen(true);
    }, [setPeekDefinitionData, setPeekDefinitionOpen]),
  });

  // Find References hook
  const { findReferencesExtension } = useFindReferences({
    workingFiles,
    onShowReferences: React.useCallback((symbolName: string, refs: Reference[]) => {
      setReferencesSymbol(symbolName);
      setReferencesResults(refs);
      setReferencesHighlight(0);
      setReferencesOpen(true);
    }, [setReferencesSymbol, setReferencesResults, setReferencesHighlight, setReferencesOpen]),
  });

  // Editor config hook (uses contexts internally)
  const { editorExtensions } = useEditorConfig({
    languageExtensions,
    lintExtension,
    handleSave,
    handleFormat,
    additionalExtensions: [goToDefinitionExtension, findReferencesExtension],
    blameData: currentBlameData,
    fileList,
    activePath,
  });

  // Breadcrumb navigation handler - expands folders and selects node
  const handleBreadcrumbNavigate = React.useCallback(
    (path: string) => {
      const node = treeIndex.byPath[path];
      if (!node) return;

      // Expand all parent folders by setting collapsed to false
      const parts = path.split('/');
      let currentPath = '';
      const expandPaths: Record<string, boolean> = {};
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
        expandPaths[currentPath] = false;
      }
      // Also expand the target if it's a folder
      if (node.type === 'folder') {
        expandPaths[path] = false;
      }
      setCollapsed((prev) => ({ ...prev, ...expandPaths }));

      // Select the node
      setSelectedNodeId(node.id);
    },
    [treeIndex, setCollapsed, setSelectedNodeId]
  );

  // Header props hook (uses contexts internally)
  const headerProps = useHeaderProps({
    serverState,
    branches,
    activeBranch,
    handleChangeBranch,
    toggleAutosave,
    toggleTheme,
    projects,
    activeProject,
    projectOverride,
    setProjectOverride,
    handleApplyProjectOverride,
    handleCommit,
    handleExportSettings,
    triggerImport,
    handleImportSettings,
    importSettingsInputRef,
    handleReloadProject,
    handleClearCache,
    handleSave,
    handleRunAll,
  });

  return (
    <div
      className={`shell ${zenMode ? 'zen-mode' : ''}`}
      role="application"
      aria-label="Atlas IDE - Code Editor"
    >
      <SkipLinks />
      <ColorBlindFilters />
      {!zenMode && <div className="ambient" aria-hidden="true" />}
      {!zenMode && <HeaderBar {...headerProps} />}

      {!zenMode && globalError && (
        <div className="error-banner" role="alert" aria-live="assertive">
          <strong>Error:</strong> {globalError}
        </div>
      )}

      <div
        className={`layout ${zenMode ? 'zen-layout' : ''}`}
        role="main"
        aria-label="Main workspace"
      >
        {panelOrder.map((panel, index) => {
          // In Zen mode, only show editor
          if (zenMode && panel !== 'editor') {
            return null;
          }
          const dropProps = getDropProps(panel);
          const dragHandleProps = getDragHandleProps(panel);
          let content: React.ReactNode = null;

          if (panel === 'sidebar') {
            content = (
              <SidebarSection
                dropProps={dropProps}
                className={getPanelClassName(panel)}
                style={getPanelStyle(panel)}
                filteredTree={filteredTree}
                collapsed={collapsed}
                selectedNodeId={selectedNodeId}
                multiSelectedIds={multiSelectedIds}
                treeFilter={treeFilter}
                fileTypeFilter={fileTypeFilter}
                availableFileTypes={availableFileTypes}
                contextMenu={contextMenu}
                isDirty={isDirty}
                onFilterChange={setTreeFilter}
                onFileTypeFilterChange={setFileTypeFilter}
                onToggleCreateMenu={toggleShowCreateMenu}
                onCreateDirectory={handleCreateDirectory}
                onCreateFile={handleCreateFile}
                onAddProject={handleAddProject}
                onCloseContextMenu={closeContextMenu}
                onContextMenu={handleContextMenu}
                onSelectNode={handleSelectNode}
                onOpenFile={handleOpenFile}
                onToggleCollapse={handleToggleCollapse}
                onRename={handleRename}
                onDelete={handleDelete}
                onNewFile={handleNewFile}
                onNewFolder={handleNewFolder}
                onMove={handleMove}
                onCopy={handleCopy}
                onCut={handleCut}
                onPaste={handlePaste}
                onDuplicate={handleDuplicate}
                hasClipboard={clipboard !== null}
                onSymbolSelect={handleSymbolSelect}
                // Recent files
                recentFiles={recentFiles}
                onRecentFileOpen={handleOpenRecentFile}
                onRecentFileRemove={removeRecentFile}
                onRecentFilesClear={clearRecentFiles}
                // Favorite files
                favoriteFiles={favoriteFiles}
                onFavoriteFileOpen={handleOpenRecentFile}
                onFavoriteFileRemove={removeFavorite}
                onFavoriteFilesClear={clearFavorites}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                // Git props
                gitFiles={gitState.files}
                gitBranch={gitState.branch}
                gitBranches={gitState.branches}
                gitAhead={gitState.ahead}
                gitBehind={gitState.behind}
                gitError={gitState.error}
                gitStagedFiles={gitStagedFiles}
                gitUnstagedFiles={gitUnstagedFiles}
                gitCommitMessage={gitCommitMessage}
                gitIsCommitting={gitIsCommitting}
                gitIsPushing={gitIsPushing}
                gitIsPulling={gitIsPulling}
                gitIsFetching={gitIsFetching}
                gitIsSyncing={gitIsSyncing}
                onGitCommitMessageChange={setGitCommitMessage}
                onGitStageFile={gitStageFile}
                onGitUnstageFile={gitUnstageFile}
                onGitStageAll={gitStageAll}
                onGitUnstageAll={gitUnstageAll}
                onGitDiscardFile={(path) => gitDiscardFile(path, (p, content) => setWorkingFiles(prev => ({ ...prev, [p]: content })))}
                onGitCommit={() => void gitCommit()}
                onGitPush={() => void gitPush()}
                onGitPull={() => void gitPull()}
                onGitFetch={() => void gitFetch()}
                onGitSync={() => void gitSync()}
                onGitSwitchBranch={(branch) => void gitSwitchBranch(branch)}
                onGitCreateBranch={(name) => void gitCreateBranch(name)}
                onGitDeleteBranch={(name) => void gitDeleteBranch(name)}
                onGitOpenFile={(path) => {
                  const node = treeIndex.byPath[path];
                  if (node) handleOpenFile(node.id);
                }}
                onGitShowDiff={(path) => {
                  setDiffViewFile(path);
                  setDiffViewOpen(true);
                }}
                getGitStatusIcon={getGitStatusIcon}
                getGitStatusColor={getGitStatusColor}
                workingFiles={{ ...persistedFiles, ...workingFiles }}
              />
            );
          } else if (panel === 'editor') {
            content = (
              <EditorSection
                dropProps={dropProps}
                dragHandleProps={dragHandleProps}
                className={getPanelClassName(panel)}
                style={getPanelStyle(panel)}
                editorExtensions={editorExtensions}
                isDirty={isDirty}
                resolvePath={resolvePath}
                pendingLocation={pendingLocation}
                editorViewRef={editorViewRef}
                onSelectTab={handleSelectTab}
                onCloseTab={handleCloseTab}
                onChange={handleCodeChange}
                onBlur={handleEditorBlur}
                onStartResize={handleStartEditorResize}
                onChangeIndent={setIndentSize}
                onChangeEncoding={setEncoding}
                goToEditorLocation={goToEditorLocation}
                setPendingLocation={setPendingLocation}
                onBreadcrumbNavigate={handleBreadcrumbNavigate}
              />
            );
          } else {
            content = (
              <PreviewSection
                dropProps={dropProps}
                dragHandleProps={dragHandleProps}
                className={getPanelClassName(panel)}
                style={getPanelStyle(panel)}
                previewHtml={previewHtml}
                previewError={previewError}
                previewSize={previewSize}
                onRestart={handleRestartPreview}
                onToggleSandbox={handleToggleSandbox}
                hasTaskEndpoint={hasTaskEndpoint}
                diffLines={diffLines}
                activePath={activePath}
                commits={commitHistorySeed}
                gitCommits={gitCommits}
                isLoadingGitCommits={gitIsLoadingCommits}
                onFetchGitCommits={gitFetchCommitLog}
                onLint={handleLint}
                onTest={handleRun}
                onBuild={handleBuild}
                getLocationFromLog={getLocationFromLog}
                onNavigateLocation={handleNavigateToLocation}
              />
            );
          }

          const dividerProps = getDividerProps(index);

          return (
            <React.Fragment key={`${panel}-${index}`}>
              {content}
              {!zenMode && dividerProps && <div key={`divider-${index}`} {...dividerProps} />}
            </React.Fragment>
          );
        })}
      </div>

      <ModalContainer
        helpTopics={helpTopics}
        goToResults={goToResults}
        handleSelectGoTo={handleSelectGoTo}
        filteredCommands={filteredCommands}
        handleSelectCommand={handleSelectCommand}
        workingFiles={workingFiles}
        persistedFiles={persistedFiles}
        projectName={activeProject || 'project'}
        onGlobalSearchSelect={handleGlobalSearchSelect}
        onSymbolSelect={handleSymbolSelect}
        onReferenceSelect={goToEditorLocation}
        gitCommits={gitCommits}
        onViewFileAtCommit={gitViewFileAtCommit}
        gitBranches={gitState.branches}
        currentBranch={gitState.branch}
        gitRemotes={gitState.remotes}
        newEntryDialog={newEntryDialog}
        newEntryName={newEntryName}
        newEntryTemplate={newEntryTemplate}
        tree={tree}
        treeIndex={treeIndex}
        setNewEntryName={setNewEntryName}
        setNewEntryTemplate={setNewEntryTemplate}
        setNewEntryDialog={setNewEntryDialog}
        applyTreeUpdate={applyTreeUpdate}
        setSelectedNodeId={setSelectedNodeId}
        setWorkingFiles={setWorkingFiles}
        conflicts={conflicts}
        onResolveConflict={resolveConflict}
        onResolveAllConflicts={resolveAllConflicts}
      />
      <OfflineIndicator />
    </div>
  );
}

export default App;
